<template>
  <div class="edo-container">
    <base-loader v-if="dataIsLoading" />

    <template v-else>
      <base-nav
        title="ЭДО"
        :show-back="true"
        back-fallback="Home"
      >
        <base-primary-button
          class="nav-btn"
          :disabled="syncButtonDisabled"
          @click="openSyncModal"
        >
          <AppIcon
            class="nav-btn__icon"
            name="sync"
            :size="16"
          />
          {{ isSelectedIntegrationSyncing ? "Синхронизация..." : "Синхронизировать" }}
        </base-primary-button>
        <EdoCreateDocumentButton
          icon="addSquare"
          class="nav-btn"
          :integration-id="selectedIntegrationId"
          label="Создать"
        />
      </base-nav>

      <EdoSabyIntegrationSwitcher
        :integrations="enabledSabyIntegrations"
        :selected-integration-id="selectedIntegrationId"
        @select="selectIntegration"
        @manage="goToSabyIntegrations"
      />

      <FilterBlock
        ref="filterBlock"
        :key="filterBlockKey"
        :search-fields="filterSearchFields"
        :initial-filters="appliedFilters"
        target-type="Edo::Document"
        :show-inactive-checkbox="true"
        show-inactive-label="Показывать архивированные записи"
        :bottom-margin="0"
        @filter-change="handleFilterChange"
      />

      <TableToolbar
        class="documents-toolbar"
        :show-settings="documents.length > 0"
        :top-margin="0"
        @open-settings="showSettingsModal = true"
      >
        <template #right-before>
          <EdoSyncStatus
            :progress="currentSyncProgress"
            :last-synced-at="lastSyncedAt"
          />
        </template>
      </TableToolbar>

      <div v-if="error" class="edo-error">
        {{ errorMessage }}
      </div>

      <BaseEmptyState
        v-if="
          !hasSelectedIntegration &&
          enabledSabyIntegrations.length > 0 &&
          !error
        "
        title="Выберите кабинет ЭДО"
        description="После выбора здесь появятся документы выбранного кабинета."
      />

      <BaseEmptyState
        v-else-if="documents.length === 0 && !error"
        title="Документов пока нет"
        description="Запустите синхронизацию, чтобы загрузить документы из ГисЭПД."
        icon="sync"
      >
        <template #actions>
          <base-primary-button
            :disabled="syncButtonDisabled"
            @click="openSyncModal"
          >
            Синхронизировать
          </base-primary-button>
        </template>
      </BaseEmptyState>

      <template v-else-if="documents.length > 0">
        <EdoDocumentsTable
          ref="edoDocumentsTable"
          :documents="documents"
          :pagination="pagination"
          :loading="dataIsLoading"
          @page-change="handlePageChange"
        />
      </template>

      <BaseDialog
        v-if="showSyncModal"
        :visible="showSyncModal"
        title="Синхронизация документов"
        :actions="syncDialogActions"
        @action="handleSyncDialogAction"
      >
        <div class="sync-form">
          <div v-if="syncModalMode === 'form'" class="sync-form__field">
            <base-label>Дата с</base-label>
            <base-input
              type="date"
              v-model="syncForm.dateFrom"
            />
          </div>

          <template v-else>
            <EdoSyncResult
              v-if="syncModalResult !== null"
              :result="syncModalResult"
            />
            <template v-else>
              <EdoSyncStatus
                :progress="currentSyncProgress"
                :last-synced-at="lastSyncedAt"
              />
              <p class="sync-form__hint">
                Окно можно закрыть — синхронизация продолжится в фоне.
              </p>
            </template>
          </template>
        </div>
      </BaseDialog>

      <Teleport to="body">
        <TableSettingsModal
          v-if="showSettingsModal"
          resource-model="Edo::Document"
          record-type="Edo::Document"
          table-name="edo-documents"
          @close="showSettingsModal = false"
          @config-saved="handleConfigSaved"
        />
      </Teleport>

    </template>
  </div>
</template>

<script>
import { createConsumer } from "@rails/actioncable";
import { mapState, mapActions } from "pinia";

import { useEdoDocumentStore } from "@/stores/edoDocumentStore";
import { useIntegrationStore } from "@/stores/integrationStore";
import { useNotificationStore } from "@/stores/notificationStore";

import EdoCreateDocumentButton from "@/components/Edo/CreateDocumentButton.vue";
import EdoDocumentsTable from "@/components/Edo/DocumentsTable.vue";
import EdoSabyIntegrationSwitcher from "@/components/Edo/SabyIntegrationSwitcher.vue";
import EdoSyncStatus from "@/components/Edo/shared/SyncStatus.vue";
import EdoSyncResult from "@/components/Edo/shared/SyncResult.vue";
import FilterBlock from "@/components/Filter/FilterBlock.vue";
import AppIcon from "@/components/Icon/AppIcon.vue";
import TableSettingsModal from "@/components/TableSettingsModal.vue";
import TableToolbar from "@/components/Table/TableToolbar.vue";
import rawSearchFields from "@/config/filters/edo/documents.json";
import { getCableUrl } from "@/utils/apiUrlUtils";
import { getCookie } from "@/utils/cookieUtils";
import {
  getSelectedSabyIntegrationId,
  selectSabyIntegrationId,
  setSelectedSabyIntegrationId,
} from "@/utils/edoIntegrationContext";
import {
  clearUrlFilters,
  filtersToApiParams,
  getCurrentPageFromUrl,
  getInitialFilters,
  updateUrlWithFilters,
} from "@/utils/filterUtils";
import { getCompanyId } from "@/utils/userUtils";

export default {
  name: "EdoDocumentsPage",
  components: {
    AppIcon,
    EdoCreateDocumentButton,
    EdoDocumentsTable,
    EdoSabyIntegrationSwitcher,
    EdoSyncStatus,
    EdoSyncResult,
    FilterBlock,
    TableSettingsModal,
    TableToolbar,
  },
  data() {
    return {
      dataIsLoading: true,
      showSyncModal: false,
      // "form" — выбор даты, "progress" — статус синхронизации до явного ОК
      syncModalMode: "form",
      syncForm: {
        dateFrom: null,
      },
      selectedIntegrationId: null,
      appliedFilters: {
        searchValues: {},
        showInactive: null,
      },
      showSettingsModal: false,
      lastSyncedAtPollingId: null,
      syncProgressWatchdogId: null,
      cable: null,
      syncModalIntegrationId: null,
      syncModalResult: null,
    };
  },
  computed: {
    ...mapState(useEdoDocumentStore, [
      "documents",
      "isLoading",
      "syncProgress",
      "error",
      "pagination",
      "lastSyncedAt",
    ]),
    // Прогресс синхронизации именно того кабинета, который сейчас открыт на
    // странице — интеграции синхронизируются независимо друг от друга
    currentSyncProgress() {
      return this.syncProgress[this.selectedIntegrationId] || null;
    },
    isSelectedIntegrationSyncing() {
      return Boolean(this.currentSyncProgress);
    },
    errorMessage() {
      if (!this.error) return null;
      return this.error?.message || "Произошла ошибка";
    },
    integrationStore() {
      return useIntegrationStore();
    },
    companyId() {
      return getCompanyId();
    },
    searchFields() {
      return rawSearchFields;
    },
    filterSearchFields() {
      return rawSearchFields.filter((field) => field.value !== "integration_id");
    },
    filterBlockKey() {
      return `edo-documents-filters-${this.selectedIntegrationId || "none"}`;
    },
    enabledSabyIntegrations() {
      return this.integrationStore.sabyIntegrations.filter(
        (integration) => integration.enabled
      );
    },
    hasSelectedIntegration() {
      return Boolean(this.selectedIntegrationId);
    },
    syncButtonDisabled() {
      return this.isSelectedIntegrationSyncing || !this.hasSelectedIntegration;
    },
    syncDialogActions() {
      if (this.syncModalMode === "progress") {
        return [
          {
            key: "close",
            label: "Закрыть",
            type: "primary",
          },
        ];
      }

      return [
        {
          key: "sync",
          label: "Синхронизировать",
          type: "primary",
          disabled: this.syncButtonDisabled,
        },
        {
          key: "close",
          label: "Отмена",
          type: "secondary",
        },
      ];
    },
  },
  watch: {
    "$route.query": {
      immediate: false,
      handler() {
        this.updateFiltersFromUrl();
        this.syncSelectedIntegrationFromFilters();
        this.loadDocuments();
      },
    },
  },
  methods: {
    ...mapActions(useEdoDocumentStore, [
      "clearDocuments",
      "fetchLastSyncedAt",
      "fetchDocuments",
      "syncDocuments",
      "applySyncProgress",
      "expireStaleSyncProgress",
    ]),

    // Открывает модалку. Если выбранная интеграция уже синхронизируется —
    // сразу показывает прогресс вместо формы выбора даты
    openSyncModal() {
      if (!this.hasSelectedIntegration) return;

      this.syncForm.dateFrom = this.defaultSyncDateFrom();
      this.syncModalIntegrationId = this.selectedIntegrationId;
      this.syncModalResult = null;
      this.syncModalMode = this.isSelectedIntegrationSyncing ? "progress" : "form";
      this.showSyncModal = true;
    },

    // Возвращает вчерашнюю локальную дату для дефолта синхронизации
    defaultSyncDateFrom() {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);

      const year = yesterday.getFullYear();
      const month = String(yesterday.getMonth() + 1).padStart(2, "0");
      const day = String(yesterday.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    // Поллит только Redis-значение синхронизации, таблицу документов не трогает
    startLastSyncedAtPolling() {
      this.stopLastSyncedAtPolling();
      this.lastSyncedAtPollingId = window.setInterval(
        () => this.fetchLastSyncedAt(),
        15000
      );
    },

    stopLastSyncedAtPolling() {
      if (!this.lastSyncedAtPollingId) return;

      window.clearInterval(this.lastSyncedAtPollingId);
      this.lastSyncedAtPollingId = null;
    },

    // Статусы из канала одновременно служат heartbeat. Если worker исчез без
    // failed, снимаем устаревшее состояние после backend Redis lease.
    startSyncProgressWatchdog() {
      this.stopSyncProgressWatchdog();
      this.syncProgressWatchdogId = window.setInterval(() => {
        const expiredIntegrationIds = this.expireStaleSyncProgress();
        if (expiredIntegrationIds.length > 0) {
          useNotificationStore().showError(
            "Синхронизация документов ЭДО прервана. Запустите её повторно."
          );
        }
      }, 60_000);
    },

    stopSyncProgressWatchdog() {
      if (!this.syncProgressWatchdogId) return;

      window.clearInterval(this.syncProgressWatchdogId);
      this.syncProgressWatchdogId = null;
    },

    // Обрабатывает действие модалки синхронизации. Синхронизация идёт в
    // фоне и закрытием модалки не отменяется — ОК просто закрывает окно
    async handleSyncDialogAction(actionKey) {
      if (actionKey === "close") {
        this.showSyncModal = false;
        this.syncModalMode = "form";
        this.syncModalIntegrationId = null;
        this.syncModalResult = null;
        return;
      }

      if (actionKey === "sync") {
        await this.handleSync();
      }
    },

    // Загружает страницу документов ЭДО с учётом query page
    async loadDocuments(page = null) {
      if (!this.hasSelectedIntegration) {
        this.clearDocuments();
        this.dataIsLoading = false;
        return;
      }

      const pageNumber =
        parseInt(page) || getCurrentPageFromUrl(this.$route);
      try {
        this.dataIsLoading = true;
        await this.fetchDocuments({
          page: pageNumber,
          ...filtersToApiParams(this.appliedFilters, this.searchFields),
          "q[archived]": this.appliedFilters.showInactive ? "all" : "active",
        });
      } finally {
        this.dataIsLoading = false;
      }
    },

    updateFiltersFromUrl() {
      this.appliedFilters = getInitialFilters(
        this.$route,
        this.searchFields,
        {
          searchValues: {},
          showInactive: false,
        }
      );
      this.appliedFilters.showInactive =
        this.appliedFilters.showInactive ||
        this.$route.query["q[archived]"] === "all";
    },

    syncSelectedIntegrationFromFilters() {
      this.selectedIntegrationId = selectSabyIntegrationId({
        routeIntegrationId:
          this.appliedFilters.searchValues.integration_id || null,
        savedIntegrationId: getSelectedSabyIntegrationId(),
        integrations: this.enabledSabyIntegrations,
      });
      this.appliedFilters = this.filtersWithIntegration(
        this.appliedFilters,
        this.selectedIntegrationId
      );
      setSelectedSabyIntegrationId(this.selectedIntegrationId);
    },

    filtersWithIntegration(
      filters = this.appliedFilters,
      integrationId = this.selectedIntegrationId
    ) {
      return {
        ...filters,
        searchValues: {
          ...filters.searchValues,
          integration_id: integrationId || "",
        },
      };
    },

    selectIntegration(integrationId) {
      const nextFilters = this.filtersWithIntegration(
        this.appliedFilters,
        integrationId
      );
      this.appliedFilters = nextFilters;
      this.syncSelectedIntegrationFromFilters();
      updateUrlWithFilters(this.$router, nextFilters, 1);
    },

    goToSabyIntegrations() {
      this.$router.push({
        name: "IntegrationSaby",
      });
    },

    handleFilterChange(filters) {
      this.appliedFilters = this.filtersWithIntegration(filters);
      this.syncSelectedIntegrationFromFilters();

      const hasValues = Object.values(this.appliedFilters.searchValues).some(
        (value) => value !== null && value !== undefined && value !== ""
      );

      if (hasValues || this.appliedFilters.showInactive) {
        updateUrlWithFilters(this.$router, this.appliedFilters, 1);
      } else {
        clearUrlFilters(this.$router);
      }
    },

    // Инициализирует интеграции и сохраняет выбранный кабинет в URL
    async initializeIntegrations() {
      if (!this.integrationStore.integrations.length) {
        await this.integrationStore.fetchIntegrations();
      }

      this.updateFiltersFromUrl();
      this.syncSelectedIntegrationFromFilters();

      if (this.selectedIntegrationId) {
        updateUrlWithFilters(this.$router, this.appliedFilters, 1);
      }
    },

    // Перезагружает список при смене страницы пагинации
    handlePageChange(page) {
      this.loadDocuments(page);
    },

    // Просит таблицу перечитать настройки колонок после сохранения
    handleConfigSaved() {
      if (this.$refs.edoDocumentsTable) {
        this.$refs.edoDocumentsTable.loadTableConfig();
      }
    },

    // Ставит синхронизацию в фон и переключает модалку на статус прогресса.
    // Модалка сама не закрывается — пользователь закрывает её кнопкой ОК
    async handleSync() {
      try {
        await this.syncDocuments({
          integrationId: this.selectedIntegrationId,
          dateFrom: this.syncForm.dateFrom,
        });
        this.syncModalIntegrationId = this.selectedIntegrationId;
        this.syncModalResult = null;
        this.syncModalMode = "progress";
      } catch {
        // API errors are shown by the global interceptor.
      }
    },

    // Подключается к EdoDocumentSyncChannel компании: прогресс синхронизации
    // приходит независимо от того, кто и когда её запустил (эта вкладка,
    // другой пользователь или минутная джоба по расписанию)
    setupSyncChannel() {
      if (this.cable) this.cable.disconnect();

      let token = getCookie("token");
      if (token && token.startsWith("Basic ")) {
        token = token.replace("Basic ", "");
      }

      this.cable = createConsumer(getCableUrl(token));
      this.cable.subscriptions.create(
        {
          channel: "EdoDocumentSyncChannel",
          company_id: this.companyId,
        },
        {
          received: (payload) => this.handleSyncProgress(payload),
        }
      );
    },

    // Применяет прогресс из канала и по завершении обновляет список/уведомляет
    handleSyncProgress(payload) {
      this.applySyncProgress(payload);

      if (payload.status === "finished") {
        if (
          this.showSyncModal &&
          this.syncModalMode === "progress" &&
          String(payload.integration_id) === String(this.syncModalIntegrationId)
        ) {
          this.syncModalResult = {
            total: payload.total ?? 0,
            succeeded: payload.succeeded ?? payload.processed ?? payload.total ?? 0,
            failed: payload.failed ?? 0,
            failedDocuments: payload.failed_documents ?? [],
          };
        }
        this.fetchLastSyncedAt();
        if (String(payload.integration_id) === String(this.selectedIntegrationId)) {
          this.loadDocuments(1);
        }
        if (payload.failed > 0) {
          useNotificationStore().showInfo("Синхронизация завершена. Есть замечания.");
        } else {
          useNotificationStore().showSuccess("Документы ЭДО синхронизированы");
        }
      }

      if (payload.status === "failed") {
        useNotificationStore().showError("Не удалось синхронизировать документы ЭДО");
      }
    },
  },
  async mounted() {
    await this.initializeIntegrations();

    const storedFilters =
      await this.$refs.filterBlock?.loadStoredFilter();

    if (storedFilters) {
      this.appliedFilters = this.filtersWithIntegration(storedFilters);
      this.syncSelectedIntegrationFromFilters();
      updateUrlWithFilters(this.$router, this.appliedFilters, 1);
    }

    await this.loadDocuments();
    await this.fetchLastSyncedAt();
    this.startLastSyncedAtPolling();
    this.startSyncProgressWatchdog();
    this.setupSyncChannel();
  },
  beforeUnmount() {
    this.stopLastSyncedAtPolling();
    this.stopSyncProgressWatchdog();
    if (this.cable) this.cable.disconnect();
  },
};
</script>

<style scoped>
.edo-container {
  flex-grow: 1;
}

.edo-nav {
  display: flex;
  align-items: center;
  margin-bottom: var(--base-out-indent);
}

.title {
  margin-right: auto;
}

.nav-btn-box {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
}

.nav-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.nav-btn__icon {
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
}

.sync-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.sync-form__field {
  display: flex;
  flex-direction: column;
}

.sync-form__hint {
  margin: 0;
  color: #64748b;
  font-size: 12px;
  line-height: 1.4;
}

.edo-error {
  color: var(--color-red, #e53e3e);
  background: #fff5f5;
  border: 1px solid #fed7d7;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: var(--base-out-indent);
}

@media (max-width: 768px) {
  .edo-nav,
  .nav-btn-box,
  .documents-toolbar {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
}
</style>
